import Konva from "konva"
import { projectPolygonToLocalMetres } from "./projection"
import { pickTileZoom, tilesForLngLatBbox, tileToLngLat, bboxLngLatFromViewport } from "./tile_math"
import { resolveSatelliteProvider } from "./satellite_providers"

// Draws a satellite backdrop under the plot polygon on the Konva stage. Tiles
// are fetched through /tiles/<provider-slug>/{z}/{x}/{y} (a server-side proxy
// that hides keys and pins the upstream host) and positioned via the same
// equirectangular projection the rest of the editor uses. The active provider
// (Esri, MapTiler, …) is chosen by the user — see satellite_providers.js.
//
// Each render() call invalidates in-flight loads from the previous call by
// bumping a generation counter — by the time an image onload fires, we
// check whether it still belongs to the current generation and drop the
// draw if not. (We can't truly abort an HTMLImageElement load, but the
// browser coalesces identical URLs into a single request.)
//
// Attribution is rendered as an HTML <div> overlay (see createAttributionEl)
// rather than a Konva.Text node: MapTiler's ToS requires the copyright text
// to link back to maptiler.com and openstreetmap.org, which a canvas text
// node cannot do. The overlay is absolutely positioned over the stage
// container by the controller.

export class SatelliteLayer {
  // tileLayer sits at the bottom of the stage; attribution is a DOM overlay
  // owned by the caller (editor_controller inserts it into the stage
  // container). attributionEl may be null in headless tests.
  constructor({ tileLayer, attributionEl }) {
    this.tileLayer = tileLayer
    this.attributionEl = attributionEl
    this._gen = 0
    // Track which provider's attribution is currently in the DOM so we only
    // rebuild the anchors when the source actually changes.
    this._attributionProviderId = null
  }

  clear() {
    this.tileLayer.destroyChildren()
    this.tileLayer.batchDraw()
    this.#setAttributionVisible(false)
  }

  // Fast path for the opacity slider: update every existing tile in place
  // instead of destroying them and re-fetching. Without this, dragging the
  // slider wipes and re-requests ~20-40 tiles per input event and the
  // canvas flickers through grey between frames.
  setOpacity(opacity) {
    const clamped = Math.max(0, Math.min(1, Number(opacity) || 0))
    for (const node of this.tileLayer.children) node.opacity(clamped)
    this.tileLayer.batchDraw()
  }

  render({ plot, scale, stageWidth, stageHeight, stageScale = 1, stagePos = { x: 0, y: 0 }, opacity, provider }) {
    this._gen += 1
    const myGen = this._gen
    this.tileLayer.destroyChildren()
    const providerCfg = resolveSatelliteProvider(provider)

    // Explicit invariants so a stage that resized to zero width (e.g. hidden
    // tab, just-mounted detached frame) doesn't reach the log2/tile math
    // with nonsense inputs.
    const pxPerMetre = scale?.pxPerMetre
    if (!plot?.polygonM?.length || !plot.originLngLat || !Number.isFinite(pxPerMetre) || pxPerMetre <= 0 || opacity <= 0) {
      this.tileLayer.batchDraw()
      this.#setAttributionVisible(false)
      return
    }

    const bbox = bboxLngLatFromViewport({ scale, stageWidth, stageHeight, originLngLat: plot.originLngLat, stageScale, stagePos })
    if (!bbox) { this.tileLayer.batchDraw(); this.#setAttributionVisible(false); return }

    // Effective pxPerMetre combines base fit scale and the current pinch
    // zoom, so pinching in picks a crisper tile zoom (and pinching out
    // picks a coarser one — fewer tiles).
    const effectivePxPerMetre = pxPerMetre * stageScale
    // Cap fetch zoom at the provider's native max so we never request tiles the
    // source doesn't serve (e.g. Esri past z19 → "tile not found" → gaps). Past
    // that, the last sharp tile is scaled up by the projection — mild blur, no
    // holes.
    const z = pickTileZoom(effectivePxPerMetre, plot.originLngLat[1], { max: providerCfg.maxZoom })
    const { xMin, yMin, xMax, yMax } = tilesForLngLatBbox(bbox, z)

    // Sanity cap: with a viewport-sized bbox a typical 1400×900 canvas at
    // z=18-19 requests ~20-40 tiles; 256 is a safety valve for broken
    // originLngLat or edge-case zoom selections. At ~30KB/JPEG tile that
    // caps a single render at ~7.5MB which is acceptable; beyond this we'd
    // rather skip than melt the free tier.
    const tileCount = (xMax - xMin + 1) * (yMax - yMin + 1)
    if (tileCount > 256 || tileCount <= 0) {
      this.tileLayer.batchDraw()
      this.#setAttributionVisible(false)
      return
    }

    const pxFor = (lng, lat) => {
      const { polygonM } = projectPolygonToLocalMetres([[lng, lat]], plot.originLngLat)
      const [xM, yM] = polygonM[0]
      return {
        x: scale.offsetPx.x + xM * scale.pxPerMetre,
        y: scale.offsetPx.y + yM * scale.pxPerMetre,
      }
    }

    for (let y = yMin; y <= yMax; y++) {
      for (let x = xMin; x <= xMax; x++) {
        const [nwLng, nwLat] = tileToLngLat(x, y, z)
        const [seLng, seLat] = tileToLngLat(x + 1, y + 1, z)
        const nwPx = pxFor(nwLng, nwLat)
        const sePx = pxFor(seLng, seLat)
        const width = sePx.x - nwPx.x
        const height = sePx.y - nwPx.y
        // Zero-area tile (degenerate polygon or mis-projection) would create
        // a node the browser may report as broken image. Skip silently —
        // we'll just miss this tile rather than flash an error.
        if (!(width > 0) || !(height > 0)) continue

        // Placeholder Konva.Image so the z-order is stable even before the
        // HTMLImageElement resolves. We upgrade it with .image(img) on load.
        const node = new Konva.Image({
          x: nwPx.x,
          y: nwPx.y,
          width,
          height,
          opacity,
          listening: false,
        })
        this.tileLayer.add(node)

        const img = new Image()
        // No crossOrigin: the /tiles/... proxy is same-origin, and setting
        // crossOrigin="anonymous" splits the HTTP cache on CORS headers the
        // proxy does not send, causing a cache miss on the second load of
        // the same tile.
        img.onload = () => {
          if (this._gen !== myGen || node.isDestroyed?.()) return
          node.image(img)
          this.tileLayer.batchDraw()
        }
        img.onerror = () => {
          // Leave the node blank — better than a broken-tile icon. The
          // proxy already returns a transparent PNG on upstream failure,
          // so this path mostly fires when MAPTILER_KEY is unset.
        }
        img.src = `/tiles/${providerCfg.tileSlug}/${z}/${x}/${y}`
      }
    }

    this.#applyAttribution(providerCfg)
    this.#setAttributionVisible(true)
    this.tileLayer.batchDraw()
  }

  #setAttributionVisible(show) {
    if (!this.attributionEl) return
    this.attributionEl.classList.toggle("hidden", !show)
  }

  // Rebuild the attribution anchors when (and only when) the provider changes.
  // The links satisfy the Esri / MapTiler ToS requirement that attribution
  // stays visible and clickable whenever their imagery is shown.
  #applyAttribution(providerCfg) {
    if (!this.attributionEl || this._attributionProviderId === providerCfg.id) return
    this._attributionProviderId = providerCfg.id
    const parts = []
    providerCfg.attribution.forEach((item, i) => {
      if (i > 0) parts.push(document.createTextNode(" "))
      // href-less entries are data-provider credits with no URL → plain text.
      if (!item.href) {
        parts.push(document.createTextNode(item.text))
        return
      }
      const a = document.createElement("a")
      a.textContent = item.text
      a.href = item.href
      a.target = "_blank"
      a.rel = "noopener"
      a.className = "text-neutral-800 hover:underline"
      parts.push(a)
    })
    this.attributionEl.replaceChildren(...parts)
  }
}

// Empty, absolutely-positioned overlay for satellite attribution. The link
// content is filled in per-provider by SatelliteLayer#applyAttribution. Sits
// over the stage container so the text stays legible regardless of zoom, pan,
// or plot fill.
export function createAttributionEl() {
  const el = document.createElement("div")
  // pointer-events-auto so the anchors stay clickable — the rest of the
  // stage container remains clickable because the overlay hugs the corner.
  el.className = "hidden absolute bottom-1 left-1 text-[10px] leading-tight px-1.5 py-0.5 bg-white/80 rounded text-neutral-800 pointer-events-auto select-none"
  return el
}
